/**
 * Altitude Validation
 * 
 * Utilities for validating that items in a session are at compatible altitudes.
 * 
 * Key Principle: Items being prioritized together should be at the same altitude
 * level to ensure apples-to-apples comparison.
 * 
 * Altitude is dimension-derived (1-5), not label-based.
 */

import type {
  AltitudeLevel,
  ItemAltitude,
} from '../types/prioritization/itemLifecycle';
import {
  LEGACY_ALTITUDE_TO_LEVEL,
  ALTITUDE_LEVELS_ORDERED,
} from '../types/prioritization/itemLifecycle';
import { formatAltitude } from './altitudeDisplay';

// Re-export the new level-based constants + category-aware helper
export {
  ALTITUDE_LEVEL_NAMES,
  ALTITUDE_LEVEL_DESCRIPTIONS,
  ALTITUDE_LEVELS_ORDERED,
  ALTITUDE_NAMES_BY_CATEGORY,
  getAltitudeName,
} from '../types/prioritization/itemLifecycle';

// ============================================================================
// Types (Updated for AltitudeLevel)
// ============================================================================

export interface AltitudeValidationResult {
  isValid: boolean;
  primaryLevel?: AltitudeLevel;
  levelCounts: Record<AltitudeLevel, number>;
  issues: AltitudeIssue[];
  suggestions: string[];
}

export interface AltitudeIssue {
  itemId: string;
  itemTitle: string;
  level?: AltitudeLevel;
  issue: 'missing' | 'mismatch';
  expectedLevel?: AltitudeLevel;
}

export interface ItemWithAltitude {
  id: string;
  title: string;
  altitudeLevel?: AltitudeLevel;
  /** @deprecated Use altitudeLevel */
  altitude?: ItemAltitude;
}

// ============================================================================
// Altitude Levels (5 levels, highest to lowest)
// ============================================================================

/**
 * Labels for altitude levels (convenience export)
 */
export const ALTITUDE_LABELS: Record<AltitudeLevel, string> = {
  5: 'Strategic',
  4: 'Program',
  3: 'Capability',
  2: 'Delivery',
  1: 'Tactical',
};

/**
 * Human-readable label for a legacy {@link ItemAltitude} string.
 * Converts the deprecated string altitude to its numeric level first, so the
 * level-keyed {@link ALTITUDE_LABELS} record can be indexed safely (indexing it
 * directly with a legacy string yields `undefined` at runtime).
 */
function legacyAltitudeLabel(altitude: ItemAltitude): string {
  return ALTITUDE_LABELS[LEGACY_ALTITUDE_TO_LEVEL[altitude]];
}

/**
 * Descriptions for altitude levels
 */
export const ALTITUDE_DESCRIPTIONS: Record<AltitudeLevel, string> = {
  5: 'Company-wide impact, 1+ year horizon, major investment',
  4: 'Multi-product impact, 3-12 month horizon, significant effort',
  3: 'Multi-workflow impact, 1-3 month horizon, moderate effort',
  2: 'Single workflow impact, weeks horizon, focused effort',
  1: 'Single screen impact, days horizon, quick wins',
};

// ============================================================================
// Legacy Constants (Deprecated - for backward compatibility)
// ============================================================================

/**
 * @deprecated Use ALTITUDE_LEVELS_ORDERED instead
 */
export const ALTITUDE_LEVELS: ItemAltitude[] = [
  'outcome',
  'initiative',
  'epic',
  'feature',
  'story',
  'task',
];

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Get the altitude level index (lower = higher level)
 */
export function getAltitudeLevel(altitude: ItemAltitude): number {
  return ALTITUDE_LEVELS.indexOf(altitude);
}

/**
 * Check if two altitudes are the same
 */
export function isSameAltitude(a: ItemAltitude, b: ItemAltitude): boolean {
  return a === b;
}

/**
 * Check if altitude A is higher than altitude B
 */
export function isHigherAltitude(a: ItemAltitude, b: ItemAltitude): boolean {
  return getAltitudeLevel(a) < getAltitudeLevel(b);
}

/**
 * Find the most common altitude in a list of items
 */
export function findPrimaryAltitude(items: ItemWithAltitude[]): ItemAltitude | undefined {
  const counts: Record<string, number> = {};
  
  for (const item of items) {
    if (item.altitude) {
      counts[item.altitude] = (counts[item.altitude] || 0) + 1;
    }
  }
  
  let maxCount = 0;
  let primaryAltitude: ItemAltitude | undefined;
  
  for (const [altitude, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      primaryAltitude = altitude as ItemAltitude;
    }
  }
  
  return primaryAltitude;
}

/**
 * Count items at each altitude level
 */
export function countAltitudes(items: ItemWithAltitude[]): Record<ItemAltitude, number> {
  const counts: Record<ItemAltitude, number> = {
    'outcome': 0,
    'initiative': 0,
    'epic': 0,
    'feature': 0,
    'story': 0,
    'task': 0,
  };
  
  for (const item of items) {
    if (item.altitude) {
      counts[item.altitude]++;
    }
  }
  
  return counts;
}

/**
 * Validate that all items in a session are at the same altitude
 * 
 * Returns validation result with issues and suggestions
 */
export function validateSessionAltitudes(
  items: ItemWithAltitude[],
  strictMode: boolean = true
): AltitudeValidationResult {
  const issues: AltitudeIssue[] = [];
  const suggestions: string[] = [];

  // Normalize each item to a numeric altitude level, preferring the current
  // altitudeLevel field and falling back to the deprecated string altitude.
  const levelOf = (item: ItemWithAltitude): AltitudeLevel | undefined =>
    item.altitudeLevel ?? (item.altitude ? LEGACY_ALTITUDE_TO_LEVEL[item.altitude] : undefined);

  // Count items at each level.
  const levelCounts: Record<AltitudeLevel, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const item of items) {
    const level = levelOf(item);
    if (level !== undefined) levelCounts[level]++;
  }

  // Find primary level (most common). Ties resolve to the higher level.
  let primaryLevel: AltitudeLevel | undefined;
  let maxCount = 0;
  for (const level of ALTITUDE_LEVELS_ORDERED) {
    if (levelCounts[level] > maxCount) {
      maxCount = levelCounts[level];
      primaryLevel = level;
    }
  }

  // Check for missing altitudes.
  const itemsWithoutAltitude = items.filter(i => levelOf(i) === undefined);
  for (const item of itemsWithoutAltitude) {
    issues.push({
      itemId: item.id,
      itemTitle: item.title,
      issue: 'missing',
      expectedLevel: primaryLevel,
    });
  }

  if (itemsWithoutAltitude.length > 0) {
    suggestions.push(
      `${itemsWithoutAltitude.length} item(s) are missing altitude. Set their altitude before prioritizing.`
    );
  }

  // Check for altitude mismatches.
  if (primaryLevel !== undefined) {
    const mismatchedItems = items.filter(i => {
      const level = levelOf(i);
      return level !== undefined && level !== primaryLevel;
    });

    for (const item of mismatchedItems) {
      issues.push({
        itemId: item.id,
        itemTitle: item.title,
        level: levelOf(item),
        issue: 'mismatch',
        expectedLevel: primaryLevel,
      });
    }

    if (mismatchedItems.length > 0) {
      // How many distinct levels are represented?
      const uniqueLevels = ALTITUDE_LEVELS_ORDERED.filter(l => levelCounts[l] > 0);

      if (uniqueLevels.length > 1) {
        suggestions.push(
          `Items are at ${uniqueLevels.length} different altitudes (${uniqueLevels.map(l => formatAltitude(l)).join(', ')}). Consider grouping by altitude for fairer comparison.`
        );

        // Suggest the majority level.
        suggestions.push(
          `Most items (${levelCounts[primaryLevel]}) are at ${formatAltitude(primaryLevel)}. Consider adjusting the ${mismatchedItems.length} item(s) at other levels.`
        );
      }
    }
  }

  // Determine if valid
  const isValid = strictMode
    ? issues.length === 0
    : issues.filter(i => i.issue === 'missing').length === 0;

  return {
    isValid,
    primaryLevel,
    levelCounts,
    issues,
    suggestions,
  };
}

/**
 * Get items that need altitude adjustment
 */
export function getItemsNeedingAltitudeAdjustment(
  items: ItemWithAltitude[],
  targetAltitude: ItemAltitude
): ItemWithAltitude[] {
  return items.filter(
    item => !item.altitude || item.altitude !== targetAltitude
  );
}

/**
 * Check if items can be hierarchically related
 * (parent must be higher altitude than children)
 */
export function canBeParentOf(
  parentAltitude: ItemAltitude,
  childAltitude: ItemAltitude
): boolean {
  return isHigherAltitude(parentAltitude, childAltitude);
}

/**
 * Get valid parent altitudes for a given child altitude
 */
export function getValidParentAltitudes(childAltitude: ItemAltitude): ItemAltitude[] {
  const childLevel = getAltitudeLevel(childAltitude);
  return ALTITUDE_LEVELS.filter((_, i) => i < childLevel);
}

/**
 * Get valid child altitudes for a given parent altitude
 */
export function getValidChildAltitudes(parentAltitude: ItemAltitude): ItemAltitude[] {
  const parentLevel = getAltitudeLevel(parentAltitude);
  return ALTITUDE_LEVELS.filter((_, i) => i > parentLevel);
}

// ============================================================================
// Individual Item Altitude Validation
// ============================================================================

/**
 * Altitude violation types
 */
export type AltitudeViolationType = 
  | 'time_horizon_mismatch'
  | 'decomposition_inconsistent'
  | 'parent_child_gap'
  | 'atomic_violation'
  | 'language_mismatch';

export interface AltitudeViolation {
  type: AltitudeViolationType;
  dimension: string;
  expected: string;
  actual: string;
  severity: 'error' | 'warning';
  message: string;
}

export interface ItemAltitudeValidationResult {
  claimed: ItemAltitude;
  suggestedAltitude?: ItemAltitude;
  isValid: boolean;
  violations: AltitudeViolation[];
  confidence: number; // 0-100
}

/**
 * Time horizon ranges for each altitude (in days)
 */
export const ALTITUDE_TIME_HORIZONS: Record<ItemAltitude, { min: number; max: number }> = {
  outcome: { min: 180, max: 730 },    // 6 months to 2 years
  initiative: { min: 90, max: 365 },  // Quarter to year
  epic: { min: 30, max: 90 },         // 1-3 months
  feature: { min: 14, max: 42 },      // 2-6 weeks
  story: { min: 1, max: 14 },         // 1 day to 2 weeks
  task: { min: 0, max: 5 },           // Same day to 5 days
};

/**
 * Language patterns that suggest altitude level
 */
export const ALTITUDE_LANGUAGE_PATTERNS: Record<ItemAltitude, { preferred: string[]; discouraged: string[] }> = {
  outcome: {
    preferred: ['become', 'achieve', 'establish', 'increase', 'reduce', 'improve'],
    discouraged: ['build', 'create', 'implement', 'fix', 'update', 'add'],
  },
  initiative: {
    preferred: ['transform', 'enable', 'launch', 'deliver', 'establish'],
    discouraged: ['code', 'test', 'debug', 'configure'],
  },
  epic: {
    preferred: ['deliver', 'complete', 'provide', 'implement'],
    discouraged: ['fix bug', 'update config', 'change setting'],
  },
  feature: {
    preferred: ['add', 'support', 'allow', 'enable', 'integrate'],
    discouraged: ['refactor', 'migrate', 'optimize'],
  },
  story: {
    preferred: ['as a', 'user can', 'system should', 'when', 'given'],
    discouraged: ['optimize', 'refactor', 'improve performance'],
  },
  task: {
    preferred: ['update', 'fix', 'add', 'remove', 'configure', 'set up'],
    discouraged: ['improve', 'enhance', 'optimize'],  // Too vague for task
  },
};

interface ItemWithDetails {
  id: string;
  title: string;
  description?: string;
  altitude?: ItemAltitude;
  parentItemId?: string;
  childItemIds?: string[];
  targetDate?: Date | string;
  timeHorizonDays?: number;
}

/**
 * Validate an item's altitude against multiple dimensions
 */
export function validateItemAltitude(
  item: ItemWithDetails,
  parentItem?: ItemWithDetails,
  childItems?: ItemWithDetails[]
): ItemAltitudeValidationResult {
  if (!item.altitude) {
    return {
      claimed: 'task', // Default
      isValid: false,
      violations: [{
        type: 'time_horizon_mismatch',
        dimension: 'altitude',
        expected: 'any altitude',
        actual: 'none',
        severity: 'error',
        message: 'Altitude is not set',
      }],
      confidence: 0,
    };
  }

  const violations: AltitudeViolation[] = [];
  let confidence = 100;

  // 1. Check time horizon match
  if (item.timeHorizonDays !== undefined) {
    const horizonCheck = checkTimeHorizon(item.altitude, item.timeHorizonDays);
    if (horizonCheck) {
      violations.push(horizonCheck);
      confidence -= 20;
    }
  }

  // 2. Check decomposition consistency (if has children)
  if (childItems && childItems.length > 0) {
    const decompCheck = checkDecompositionConsistency(item.altitude, childItems);
    violations.push(...decompCheck);
    confidence -= decompCheck.length * 15;
  }

  // 3. Check parent-child gap (if has parent)
  if (parentItem && parentItem.altitude) {
    const gapCheck = checkParentChildGap(item.altitude, parentItem.altitude);
    if (gapCheck) {
      violations.push(gapCheck);
      confidence -= 20;
    }
  }

  // 4. Atomic test (tasks cannot have children)
  if (item.altitude === 'task' && childItems && childItems.length > 0) {
    violations.push({
      type: 'atomic_violation',
      dimension: 'decomposability',
      expected: 'no children (atomic)',
      actual: `${childItems.length} child(ren)`,
      severity: 'error',
      message: 'Tasks are atomic and cannot have sub-items. Consider changing altitude to Story.',
    });
    confidence -= 30;
  }

  // 5. Check language patterns
  const languageCheck = checkLanguagePatterns(item.altitude, item.title, item.description);
  if (languageCheck) {
    violations.push(languageCheck);
    confidence -= 10;
  }

  // Suggest altitude if confidence is low
  let suggestedAltitude: ItemAltitude | undefined;
  if (confidence < 70) {
    suggestedAltitude = suggestAltitude(item, parentItem, childItems);
  }

  return {
    claimed: item.altitude,
    suggestedAltitude: suggestedAltitude !== item.altitude ? suggestedAltitude : undefined,
    isValid: violations.filter(v => v.severity === 'error').length === 0,
    violations,
    confidence: Math.max(0, confidence),
  };
}

/**
 * Check if time horizon matches the altitude
 */
function checkTimeHorizon(altitude: ItemAltitude, days: number): AltitudeViolation | null {
  const range = ALTITUDE_TIME_HORIZONS[altitude];
  
  if (days < range.min) {
    return {
      type: 'time_horizon_mismatch',
      dimension: 'time_horizon',
      expected: `${range.min}-${range.max} days`,
      actual: `${days} days`,
      severity: 'warning',
      message: `Time horizon (${days} days) is shorter than typical for ${legacyAltitudeLabel(altitude)} level`,
    };
  }
  
  if (days > range.max) {
    return {
      type: 'time_horizon_mismatch',
      dimension: 'time_horizon',
      expected: `${range.min}-${range.max} days`,
      actual: `${days} days`,
      severity: 'warning',
      message: `Time horizon (${days} days) is longer than typical for ${legacyAltitudeLabel(altitude)} level`,
    };
  }
  
  return null;
}

/**
 * Check if children are at the correct altitude relative to parent
 */
function checkDecompositionConsistency(
  parentAltitude: ItemAltitude,
  childItems: ItemWithDetails[]
): AltitudeViolation[] {
  const violations: AltitudeViolation[] = [];
  const parentLevel = getAltitudeLevel(parentAltitude);
  const expectedChildLevel = parentLevel + 1;
  const expectedChildAltitude = ALTITUDE_LEVELS[expectedChildLevel];

  for (const child of childItems) {
    if (!child.altitude) continue;
    
    const childLevel = getAltitudeLevel(child.altitude);
    
    if (childLevel !== expectedChildLevel) {
      violations.push({
        type: 'decomposition_inconsistent',
        dimension: 'decomposition',
        expected: expectedChildAltitude || 'one level below',
        actual: child.altitude,
        severity: childLevel < parentLevel ? 'error' : 'warning',
        message: `Child "${child.title}" is at ${legacyAltitudeLabel(child.altitude)} level, expected ${expectedChildAltitude ? legacyAltitudeLabel(expectedChildAltitude) : 'one level below'}`,
      });
    }
  }

  return violations;
}

/**
 * Check parent-child altitude gap (should be exactly 1 level)
 */
function checkParentChildGap(
  childAltitude: ItemAltitude,
  parentAltitude: ItemAltitude
): AltitudeViolation | null {
  const parentLevel = getAltitudeLevel(parentAltitude);
  const childLevel = getAltitudeLevel(childAltitude);
  const gap = childLevel - parentLevel;

  if (gap <= 0) {
    return {
      type: 'parent_child_gap',
      dimension: 'hierarchy',
      expected: 'child below parent',
      actual: `child at same or higher level`,
      severity: 'error',
      message: `Item at ${legacyAltitudeLabel(childAltitude)} cannot be child of ${legacyAltitudeLabel(parentAltitude)}`,
    };
  }

  if (gap > 1) {
    return {
      type: 'parent_child_gap',
      dimension: 'hierarchy',
      expected: 'adjacent levels',
      actual: `${gap} levels apart`,
      severity: 'warning',
      message: `Item skips ${gap - 1} altitude level(s). Consider adding intermediate items.`,
    };
  }

  return null;
}

/**
 * Check if language patterns match the altitude
 */
function checkLanguagePatterns(
  altitude: ItemAltitude,
  title: string,
  description?: string
): AltitudeViolation | null {
  const patterns = ALTITUDE_LANGUAGE_PATTERNS[altitude];
  const text = `${title} ${description || ''}`.toLowerCase();

  // Check for discouraged patterns
  for (const discouraged of patterns.discouraged) {
    if (text.includes(discouraged.toLowerCase())) {
      return {
        type: 'language_mismatch',
        dimension: 'language',
        expected: `language typical for ${legacyAltitudeLabel(altitude)}`,
        actual: `uses "${discouraged}" which is atypical`,
        severity: 'warning',
        message: `Language "${discouraged}" is uncommon for ${legacyAltitudeLabel(altitude)} level items`,
      };
    }
  }

  return null;
}

/**
 * Suggest an appropriate altitude based on item characteristics
 */
function suggestAltitude(
  item: ItemWithDetails,
  parentItem?: ItemWithDetails,
  childItems?: ItemWithDetails[]
): ItemAltitude {
  // If has parent, suggest one level below parent
  if (parentItem?.altitude) {
    const parentLevel = getAltitudeLevel(parentItem.altitude);
    const suggestedLevel = Math.min(parentLevel + 1, ALTITUDE_LEVELS.length - 1);
    return ALTITUDE_LEVELS[suggestedLevel];
  }

  // If has children, suggest one level above highest child
  if (childItems && childItems.length > 0) {
    const childAltitudes = childItems
      .map(c => c.altitude)
      .filter((a): a is ItemAltitude => a !== undefined);
    
    if (childAltitudes.length > 0) {
      const highestChildLevel = Math.min(...childAltitudes.map(getAltitudeLevel));
      const suggestedLevel = Math.max(0, highestChildLevel - 1);
      return ALTITUDE_LEVELS[suggestedLevel];
    }
  }

  // Base on time horizon
  if (item.timeHorizonDays !== undefined) {
    for (const [altitude, range] of Object.entries(ALTITUDE_TIME_HORIZONS)) {
      if (item.timeHorizonDays >= range.min && item.timeHorizonDays <= range.max) {
        return altitude as ItemAltitude;
      }
    }
  }

  // Default to feature (middle ground)
  return 'feature';
}

/**
 * Get altitude violations for an item (convenience function)
 */
export function getAltitudeViolations(
  item: ItemWithDetails,
  parentItem?: ItemWithDetails,
  childItems?: ItemWithDetails[]
): AltitudeViolation[] {
  return validateItemAltitude(item, parentItem, childItems).violations;
}

// ============================================================================
// Session Integration
// ============================================================================

/**
 * Validate session candidates for altitude consistency
 * Returns a summary suitable for UI display
 */
export function validateSessionCandidatesAltitude(
  candidates: ItemWithAltitude[]
): {
  canProceed: boolean;
  warningLevel: 'none' | 'info' | 'warning' | 'error';
  message: string;
  details?: string[];
} {
  if (candidates.length === 0) {
    return {
      canProceed: true,
      warningLevel: 'none',
      message: 'No candidates to validate',
    };
  }
  
  const result = validateSessionAltitudes(candidates, false);
  
  // Check for missing altitudes
  const missingCount = result.issues.filter(i => i.issue === 'missing').length;
  if (missingCount > 0) {
    return {
      canProceed: false,
      warningLevel: 'error',
      message: `${missingCount} item${missingCount !== 1 ? 's' : ''} missing altitude`,
      details: [
        'All items must have an altitude set before prioritization.',
        'Use the "Set Altitude" button below to fix this without leaving the page.',
      ],
    };
  }
  
  // Check for mismatches
  const mismatchCount = result.issues.filter(i => i.issue === 'mismatch').length;
  if (mismatchCount > 0) {
    const uniqueAltitudes = Object.entries(result.levelCounts)
      .filter(([, count]) => count > 0)
      .length;

    if (uniqueAltitudes > 2) {
      return {
        canProceed: false,
        warningLevel: 'warning',
        message: `Items are at ${uniqueAltitudes} different altitude levels`,
        details: [
          'Prioritizing items at very different levels may lead to unfair comparisons.',
          `Consider grouping items by altitude or adjusting to match the majority${result.primaryLevel ? ` (${formatAltitude(result.primaryLevel)})` : ''}.`,
        ],
      };
    }
    
    return {
      canProceed: true,
      warningLevel: 'info',
      message: `Items are at ${uniqueAltitudes} altitude levels`,
      details: [
        'Some items are at different altitude levels.',
        'The results will still be valid, but consider if items are truly comparable.',
      ],
    };
  }
  
  return {
    canProceed: true,
    warningLevel: 'none',
    message: `All ${candidates.length} items are at ${result.primaryLevel ? formatAltitude(result.primaryLevel) : 'the same altitude'}`,
  };
}

// ============================================================================
// Leveling Constraints (Structural Rules)
// ============================================================================

import type {
  LevelingConstraintViolation,
  LevelingConstraintType,
} from '../types/prioritization/leveling';
import type { Candidate } from '../types/prioritization/lifecycle';
import { computeAltitudeLevel, legacyAltitudeToLevel } from './altitudePrediction';

/**
 * Altitude levels that require a parent or portfolio container
 * (Cannot exist meaningfully in isolation)
 * Levels 4-5 (Program, Strategic) need structural context
 */
const HIGH_LEVELS_REQUIRING_PARENT: AltitudeLevel[] = [4, 5];

/**
 * Altitude levels that require completion requirements
 * Levels 4-5 (Program, Strategic) need completion info
 */
const LEVELS_REQUIRING_COMPLETION: AltitudeLevel[] = [4, 5];

/**
 * Get the effective altitude level for a candidate
 * Uses altitudeLevel if set, otherwise computes from scoping inputs,
 * otherwise falls back to legacy altitude conversion
 */
function getEffectiveAltitudeLevel(candidate: Candidate): AltitudeLevel | null {
  // First try the new altitudeLevel field
  if (candidate.altitudeLevel) {
    return candidate.altitudeLevel;
  }
  
  // Try computing from scoping inputs
  if (candidate.altitudeScopingInputs) {
    return computeAltitudeLevel(candidate.altitudeScopingInputs) ?? null;
  }
  
  // Fall back to legacy altitude conversion
  const legacyAltitude = candidate.canonicalAltitude || candidate.altitude;
  if (legacyAltitude) {
    return legacyAltitudeToLevel(legacyAltitude) || null;
  }
  
  return null;
}

/**
 * Validate standalone constraints for an item
 * 
 * High-altitude items (Levels 4-5: Program, Strategic) must have:
 * - A parent item, OR
 * - A portfolio container (strategic theme, program, objective)
 * 
 * This prevents large, vague items from being treated as directly comparable work.
 */
export function validateStandaloneConstraints(
  candidate: Candidate
): LevelingConstraintViolation | null {
  const level = getEffectiveAltitudeLevel(candidate);
  
  // No altitude set - can't validate
  if (!level) {
    return null;
  }
  
  // Check if this level requires a parent (levels 4-5)
  if (!HIGH_LEVELS_REQUIRING_PARENT.includes(level)) {
    return null; // Standalone allowed
  }
  
  // Check for parent item
  if (candidate.parentItemId) {
    return null; // Has parent - valid
  }
  
  // Check for portfolio container (strategic bucket or org unit)
  // These serve as implicit containers for high-altitude items
  if (candidate.strategicBucket || candidate.orgUnitId) {
    return null; // Has container - valid
  }
  
  // Violation: high-altitude item without parent or container
  return {
    itemId: candidate.id,
    itemTitle: candidate.title,
    constraintType: 'standalone_violation',
    message: `Level ${level} (${ALTITUDE_LABELS[level]}) items must have a parent item or belong to a strategic container.`,
    severity: 'warning',
    suggestedAction: 'Add a parent item or assign to a strategic bucket.',
  };
}

/**
 * Validate completion requirements for high-altitude items
 * 
 * Levels 4-5 (Program, Strategic) require at least one of:
 * - Success definition (measurable outcome)
 * - First deliverable (concrete milestone)
 * - Owner (accountable person/function)
 */
export function validateCompletionRequirements(
  candidate: Candidate
): LevelingConstraintViolation | null {
  const level = getEffectiveAltitudeLevel(candidate);
  
  // No altitude set - can't validate
  if (!level) {
    return null;
  }
  
  // Check if this level requires completion info (levels 4-5)
  if (!LEVELS_REQUIRING_COMPLETION.includes(level)) {
    return null; // Not required for this level
  }
  
  // Check completion requirements
  const requirements = candidate.altitudeCompletionRequirements;
  if (requirements) {
    const hasAny = !!(
      requirements.successDefinition ||
      requirements.firstDeliverable ||
      requirements.owner
    );
    
    if (hasAny) {
      return null; // Has at least one - valid
    }
  }
  
  // Also check legacy fields that might contain equivalent info
  if (candidate.desiredOutcome) {
    return null; // Has desired outcome - counts as success definition
  }
  
  // Violation: missing completion requirements
  return {
    itemId: candidate.id,
    itemTitle: candidate.title,
    constraintType: 'completion_missing',
    message: `Level ${level} (${ALTITUDE_LABELS[level]}) items require a success definition, first deliverable, or assigned owner.`,
    severity: 'warning',
    suggestedAction: 'Add at least one: success criteria, first deliverable, or accountable owner.',
  };
}

/**
 * Validate parent-child altitude gap
 * 
 * Parent items should be exactly 1 altitude level above children.
 * Example: Level 4 → Level 3 (valid), Level 5 → Level 1 (invalid - too big a gap)
 */
export function validateParentChildGap(
  candidate: Candidate,
  parentCandidate?: Candidate
): LevelingConstraintViolation | null {
  if (!parentCandidate) {
    return null; // No parent to validate
  }
  
  const childLevel = getEffectiveAltitudeLevel(candidate);
  const parentLevel = getEffectiveAltitudeLevel(parentCandidate);
  
  if (!childLevel || !parentLevel) {
    return null; // Can't validate without altitudes
  }
  
  // Gap is how many levels apart (parent should be 1 higher than child)
  const gap = parentLevel - childLevel;
  
  // Valid: parent is exactly 1 level above child
  if (gap === 1) {
    return null;
  }
  
  // Invalid gap - child is same or higher than parent
  if (gap <= 0) {
    return {
      itemId: candidate.id,
      itemTitle: candidate.title,
      constraintType: 'parent_gap_violation',
      message: `Child (Level ${childLevel} - ${ALTITUDE_LABELS[childLevel]}) cannot be at the same or higher level than parent (Level ${parentLevel} - ${ALTITUDE_LABELS[parentLevel]}).`,
      severity: 'error',
      suggestedAction: `Change to a level below ${ALTITUDE_LABELS[parentLevel]}.`,
    };
  }
  
  // Gap is too large (more than 2 levels apart)
  if (gap > 2) {
    return {
      itemId: candidate.id,
      itemTitle: candidate.title,
      constraintType: 'parent_gap_violation',
      message: `Gap between Level ${parentLevel} (${ALTITUDE_LABELS[parentLevel]}) and Level ${childLevel} (${ALTITUDE_LABELS[childLevel]}) is too large (${gap} levels). Consider adding intermediate items.`,
      severity: 'warning',
      suggestedAction: 'Add intermediate items to bridge the altitude gap.',
    };
  }
  
  return null; // Gap of 2 is acceptable (e.g., skipping a level)
}

/**
 * Validate atomic constraint for Level 1 (Tactical) items
 * 
 * Level 1 items are atomic - they cannot have children.
 */
export function validateAtomicConstraint(
  candidate: Candidate
): LevelingConstraintViolation | null {
  const level = getEffectiveAltitudeLevel(candidate);
  
  // Only Level 1 (Tactical) items are atomic
  if (level !== 1) {
    return null;
  }
  
  if (candidate.childItemIds && candidate.childItemIds.length > 0) {
    return {
      itemId: candidate.id,
      itemTitle: candidate.title,
      constraintType: 'atomic_violation',
      message: 'Level 1 (Tactical) items are atomic and cannot have child items.',
      severity: 'error',
      suggestedAction: 'Change to Level 2 or higher, or remove child items.',
    };
  }
  
  return null;
}

/**
 * Run all leveling constraint validations for a candidate
 */
export function validateLevelingConstraints(
  candidate: Candidate,
  parentCandidate?: Candidate
): LevelingConstraintViolation[] {
  const violations: LevelingConstraintViolation[] = [];
  
  const standalone = validateStandaloneConstraints(candidate);
  if (standalone) violations.push(standalone);
  
  const completion = validateCompletionRequirements(candidate);
  if (completion) violations.push(completion);
  
  const parentGap = validateParentChildGap(candidate, parentCandidate);
  if (parentGap) violations.push(parentGap);
  
  const atomic = validateAtomicConstraint(candidate);
  if (atomic) violations.push(atomic);
  
  return violations;
}

/**
 * Validate leveling constraints for all candidates
 * 
 * Returns all violations grouped by constraint type.
 */
export function validateAllLevelingConstraints(
  candidates: Candidate[]
): {
  violations: LevelingConstraintViolation[];
  byType: Record<LevelingConstraintType, LevelingConstraintViolation[]>;
  summary: {
    total: number;
    errors: number;
    warnings: number;
  };
} {
  const candidatesById = new Map(candidates.map(c => [c.id, c]));
  const violations: LevelingConstraintViolation[] = [];
  
  for (const candidate of candidates) {
    const parent = candidate.parentItemId 
      ? candidatesById.get(candidate.parentItemId) 
      : undefined;
    
    const itemViolations = validateLevelingConstraints(candidate, parent);
    violations.push(...itemViolations);
  }
  
  // Group by type
  const byType: Record<LevelingConstraintType, LevelingConstraintViolation[]> = {
    standalone_violation: [],
    completion_missing: [],
    altitude_mismatch: [],
    parent_gap_violation: [],
    atomic_violation: [],
  };
  
  for (const v of violations) {
    byType[v.constraintType].push(v);
  }
  
  // Summary
  const errors = violations.filter(v => v.severity === 'error').length;
  const warnings = violations.filter(v => v.severity === 'warning').length;
  
  return {
    violations,
    byType,
    summary: {
      total: violations.length,
      errors,
      warnings,
    },
  };
}
